import i18n from '@/i18n';
import Auth from '@/services/auth/auth';
import debounce from 'lodash/debounce';
import { PERMISSION, hasPermission } from '@/permissions';
import { decodeGroupedOrderItems, decodeOrder } from '@/utils/decoding';
import { getSessionId } from '@/utils/session';
import axios, { AxiosInstance } from 'axios';
import { ActionTree } from 'vuex';
import { handleAllErrors } from '../../utils/errorHandling';
import { AuthState, RootState } from '../types';
import { state } from './auth';

//TODO: We are using a deboucer on the build local menu since we update the item and rebuilding the menu before we add the options and tags.
// Keyed by restaurant id so concurrent requests for different tenants get independent timers
// (a single shared debounce would let the latest call clobber the previous one's args).
const debouncedBuildLocalMenuById: Map<string, () => void> = new Map();
let queueLocalMenuRebuild: ((restaurantId: string) => void) | null = null;

export const actions: ActionTree<AuthState, RootState> = {

	/**
	 * Update axios instance with the tenant id and token
	 *
	 * @return {void}
	 */
	updateAxiosInstance({ commit, dispatch }): void {
		const axiosInst = axios.create({
			baseURL: process.env.VUE_APP_API_AUDIENCE,
			headers: {
				'X-RESTAURANT-ID': state.tenantId!,
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${state.accessToken}`,
				'Locale': i18n.locale,
				'X-SESSION-ID': getSessionId()
			}
		});
		// Setup an interceptor to rebuild the local menu when a post, put or delete request is made
		if (process.env.NODE_ENV === 'development') {
			queueLocalMenuRebuild = (restaurantId: string): void => {
				let debounced: (() => void) | undefined = debouncedBuildLocalMenuById.get(restaurantId);
				if (!debounced) {
					debounced = debounce(() => {
						dispatch('menus/buildLocalMenu', restaurantId, { root: true });
					}, 1000);
					debouncedBuildLocalMenuById.set(restaurantId, debounced);
				}
				debounced();
			};

			axiosInst.interceptors.response.use(response => {
				if (response.statusText === 'Created' || response.statusText === 'OK' && response.config && (response.config.method === 'post' || response.config.method === 'put' || response.config.method === 'delete')) {
					// Pull the id from the request that just succeeded — per-request overrides take precedence
					// over the instance default, so this is always the schema the backend actually operated on.
					// AxiosHeaders normalizes keys to lowercase internally, so use .get() for case-insensitive lookup.
					const headerId: string | undefined = response.config.headers?.get?.('X-RESTAURANT-ID') as string | undefined;
					queueLocalMenuRebuild!(headerId || state.restaurant.id);
				}
				return response;
			}, error => {
				return Promise.reject(error);
			});
		}
		commit('SET_AXIOS_INSTANCE', axiosInst);
	},

	/**
	 * Set restaurants info needed from the backend
	 *
	 * @return {Promise<void>}
	 */
	async getRestaurantsInfo({ commit }, payload: { authRestaurants: AuthRestaurants[], groups?: string }): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		const { authRestaurants, groups } = payload;
		try {
			const restaurants = await axiosInst.post(`/restaurants/list${groups ? `?groups=${groups}` : ''}`, authRestaurants);
			commit('SET_RESTAURANTS', JSON.stringify(restaurants.data));
			commit('SET_AUTH_GROUPS', groups);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurants with menus names for menu replication
	 *
	 * @return {Promise<Tenant[] | void>}
	 */
	async getRestaurantsWithMenus({}, payload: { ids: string[], k12: boolean }): Promise<Tenant[] | void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			const { data } = await axiosInst.post<Tenant[]>('/restaurants/list-with-menus', payload);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch restaurant
	 *
	 * @return {Promise<void>}
	 */
	async fetchRestaurant({ commit }, { tenantId }): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			if(state.accessToken && state.tenantId) {
				const restaurant = await axiosInst.get(`/restaurants/${tenantId}`);
				commit('SET_RESTAURANT', restaurant.data);
				commit('SET_POS_INTEGRATED');
				commit('customQuestions/SET_CUSTOM_QUESTIONS', restaurant.data.custom_questions, { root: true });
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update a restaurant
	 *
	 * @param {Restaurant} restaurant
	 * @return {Promise<void>}
	 */
	async updateRestaurant({ commit }, { restaurant }): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			const restaurantUpdated: Restaurant = (await axiosInst.put(`/restaurants/${restaurant.id}`, { ...restaurant, force_publish: true })).data;
			commit('SET_RESTAURANT', restaurantUpdated);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Upload image to S3 and update the a restaurant's image accordingly
	 *
	 * @return {Promise<void>}
	 */
	async uploadRestaurantImage ({ }, { restaurant, image }) {
		const axiosInst: AxiosInstance = state.axiosInst;

		const formData = new FormData();
		formData.append('file', image[0], image[0].name);

		try {
			await axiosInst.post(`/restaurants/${restaurant.id}/image`, formData, {
				headers: {
					'Content-Disposition': `form-data; name="file"; filename="${image[0].name}"`,
					'Content-Type': 'multipart/form-data'
				}
			});
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Set the tenantId
	 *
	 * @param {LoginCredentials} credentials
	 * @return {Promise<void>}
	 */
	async setTenant({ commit, dispatch }, tenantId: string): Promise<void> {
		commit('SET_TENANT_ID', tenantId);
		Auth.setTenantIdInStorage(tenantId);
		dispatch('updateAxiosInstance');
		dispatch('orders/setItemDiets', [], { root: true });
		dispatch('orders/resetOrderState', null, { root: true });
		if (!hasPermission(PERMISSION.SESSION_STAFF, state.roleLevel, false)) {
			await dispatch('menus/fetchMenus', {}, { root: true });
		}
	},

	/**
	 * Toggle the switching-location flag. While true, the main router-view is
	 * unmounted so no page can fire requests against a half-switched tenant.
	 *
	 * @param {boolean} switchingLocation
	 * @return {void}
	 */
	setSwitchingLocation({ commit }, switchingLocation: boolean): void {
		commit('SET_SWITCHING_LOCATION', switchingLocation);
	},

	/**
	 * Login the user with credentials
	 *
	 * @param {LoginCredentials} credentials
	 * @return {Promise<void>}
	 */
	login({ }, credentials: LoginCredentials): Promise<void> {
		return Auth.login({ username: credentials.username!, password: credentials.password! });
	},

	/**
	 * Login the user with tokens provided from the app by injection
	 *
	 * @param {LoginTokens} tokens
	 * @return {Promise<void>}
	 */
	async loginAppUser({ commit }, tokens: LoginTokens): Promise<boolean> {
		const isAuthenticated = await Auth.loginAppUser({ accessToken: tokens.accessToken, idToken: tokens.idToken });
		commit('SET_AUTHENTICATED', isAuthenticated);
		commit('SET_EMPLOYEE_ID', tokens.employeeId);
		commit('SET_EMPLOYEE_NAME', tokens.employeeName);
		commit('SET_CREATED_BY', tokens.createdBy);
		commit('SET_PIN_ROLE', tokens.pinRole);
		return isAuthenticated;
	},


	/**
	 * Authenticate the logged in user with info from url
	 *
	 * @return {Promise<void>}
	 */
	async authenticate({ dispatch, commit }): Promise<void> {
		try {
			const loggedInEmail = await Auth.handleAuthentication();
			commit('SET_STAFF_EMAIL', loggedInEmail);
			commit('SET_AUTHENTICATED', true);
			dispatch('updateAxiosInstance');
		}
		catch (error) {
			commit('SET_AUTHENTICATED', false);
			throw error;
		}
	},

	/**
	 * Send reset password request
	 *
	 * @param {string} email
	 */
	async resetPassword({ }, email: string): Promise<any> {
		return await new Promise((resolve, reject) => {
			Auth.resetPassword(email)
				.then(response => {
					resolve(response);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	/**
	 * Logout the user and clear all storage
	 *
	 * @return {void}
	 */
	logout({ commit }): void {
		commit('authenticated', false);
		Auth.logout();
	},

	/**
	 * Clear tokens and user info from local storage,
	 * cookies and vuex store
	 *
	 * @return {void}
	 */
	clear({ commit, dispatch }): void {
		Auth.clear();
		commit('SET_ACCESS_TOKEN', null);
		commit('SET_ID_TOKEN', null);
		commit('SET_EXPIRES_AT', null);
		commit('SET_AUTHENTICATED', false);
		// TODO - add a prop to the user metadata so they can choose their default location
		commit('SET_TENANT_ID', null);
		commit('SET_RESTAURANTS', null);
		commit('SET_AUTH_GROUPS', null);
		commit('SET_STAFF_EMAIL', null);
		dispatch('updateAxiosInstance');
	},

	/**
	 * Clear single sign on from local storage,
	 * cookies and vuex store
	 *
	 * @return {void}
	 */
	clearSSO({ commit }): void {
		Auth.clearSSO();
		commit('SET_LAST_LOGIN', null);
	},

	/**
	 * Fetch orders report of the restaurant
	 *
	 * @param {GetReportQueryParams} queryParams
	 * @param {ExportReportQueryParams} queryParams
	 * @return {Promise<{ url: string }>}
	 */
	async exportOrdersReport({}, queryParams: ExportReportQueryParams): Promise<{ url: string } | undefined> {
		const axiosInst: AxiosInstance = state.axiosInst;
		const { dateRange, includePrices, locations, sort, createdAtDateRange } = queryParams;
		try {
			// Temporarily not awaiting here and simply returning, the endpoint will keep running and send an email later... THIS IS A HACK.
			axiosInst.get<{ url: string }>('/orders/export', { params: {
				startDate: dateRange[0] || undefined,
				endDate: dateRange[1] || undefined,
				createdStartDate: createdAtDateRange[0] || undefined,
				createdEndDate: createdAtDateRange[1] || undefined,
				locations: locations ? JSON.stringify(locations) : undefined,
				includePrices: includePrices || false,
				email: state.staffEmail,
				sort
			} });
			return { url: '' };
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch orders report of the restaurant
	 *
	 * @param {GetReportQueryParams} queryParams
	 * @return {Promise<{ groupedItems: OrderItemGroup[], orders: EventSuiteOrder[]} | undefined>}
	 */
	async fetchOrdersReport({}, queryParams: GetReportQueryParams): Promise<{ groupedItems: OrderItemGroup[], orders: EventSuiteOrder[]} | undefined> {
		const axiosInst: AxiosInstance = state.axiosInst;
		const { dateRange, school, createdAtDateRange } = queryParams;
		try {
			const { data } = await axiosInst.get<{ groupedItems: OrderItemGroup[], orders: EventSuiteOrder[]}>('/orders/report', { params: {
				startDate: dateRange[0] || undefined,
				endDate: dateRange[1] || undefined,
				createdStartDate: createdAtDateRange[0] || undefined,
				createdEndDate: createdAtDateRange[1] || undefined,
				school: school || undefined
			} });
			return {
				groupedItems: decodeGroupedOrderItems(data.groupedItems),
				orders: data.orders.map(decodeOrder)
			};
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Toggle orders paused for a target location.
	 * When locationId is omitted, operates on the currently-active tenant (OrderManagement usage).
	 * When locationId is provided, operates on any location the user has access to (Locations list usage).
	 * The X-RESTAURANT-ID header is overridden on the request so the backend tenant middleware
	 * routes the write (and the data.json rebuild it triggers) to the correct schema.
	 *
	 * @param {{ message?: OrdersPausedMessage, deleteMessage?: boolean, locationId?: string }} [payload]
	 * @return {Promise<void>}
	 */
	async toggleOrdersPaused({ commit, state }, payload?: { message?: OrdersPausedMessage, deleteMessage?: boolean, locationId?: string }): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		const { message, deleteMessage, locationId } = payload || {};
		const targetId: string = locationId ?? state.tenantId!;
		const isCurrent: boolean = targetId === state.tenantId;

		// Read source state: state.restaurant for current tenant, matching tenant in state.restaurants otherwise.
		const target: Restaurant | Tenant | undefined = isCurrent
			? state.restaurant
			: state.restaurants?.find((t: Tenant) => t.id === targetId);

		if (!target) {
			return;
		}

		try {
			const requestBody: Record<string, any> = {
				orders_paused: !target.orders_paused,
				force_publish: true
			};

			if (deleteMessage) {
				// Add existing order_options to the request body
				requestBody.order_options = {
					...(target.order_options || {})
				};

				// Remove orders_paused_message from order_options if it exists
				delete requestBody.order_options.orders_paused_message;

				// Remove orders_paused_message from all locales
				if (requestBody.order_options?.localization) {
					const localizationObj = Object.keys(target.order_options?.localization || {}).reduce((acc, key) => {
						if (target.order_options?.localization?.[key]?.orders_paused_message) {
							acc[key] = {
								...(target.order_options?.localization?.[key] || {})
							};
							delete acc[key].orders_paused_message;

							// If the locale is empty, delete it
							if (Object.keys(acc[key]).length === 0) {
								delete acc[key];
							}
						}
						return acc;
					}, target.order_options?.localization || {});

					// If the localization object is empty, delete it
					if (Object.keys(localizationObj).length === 0) {
						delete requestBody.order_options.localization;
					}
					else {
						// Add the localization object to the request body
						requestBody.order_options.localization = localizationObj;
					}
				}
			}
			else if (message) {
				// Add orders_paused_message to order_options
				requestBody.order_options = {
					...(target.order_options || {}),
					orders_paused_message: message.orders_paused_message
				};

				// If there is localization for the orders_paused_message, add it to the request body
				if (message.localization) {
					const localizationObj = { ...(target.order_options?.localization || {}) };
					for (const [locale, values] of Object.entries(message.localization)) {
						localizationObj[locale] = {
							...localizationObj[locale],
							orders_paused_message: values.orders_paused_message
						};
					}

					// Add the localization object to the request body
					requestBody.order_options.localization = localizationObj;
				}
			}

			// Send the updated request — override X-RESTAURANT-ID so the backend operates on the
			// target tenant's schema (the shared axios instance is pinned to the current tenant).
			const restaurantUpdated: Restaurant = (await axiosInst.put(`/restaurants/${targetId}`, requestBody, {
				headers: { 'X-RESTAURANT-ID': targetId }
			})).data;

			// For the current tenant, replace state.restaurant with the full server response (preserves legacy semantics).
			if (isCurrent) {
				commit('SET_RESTAURANT', restaurantUpdated);
			}

			// Refetch the restaurants list so every tenant in state.restaurants reflects the latest
			// server state, not just the one we toggled. This also picks up any concurrent changes
			// another user may have made to other locations while this session was open.
			await Auth.refreshRestaurantsFromToken();
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch the credits of the location
	 *
	 * @param {TableFilterParams} filterParams - Optional filter parameters for pagination, sorting, and search
	 * @return {Promise<PaginatedResults<App8Credit> | void>}
	 */
	async getCredits(_context, filterParams?: TableFilterParams): Promise<PaginatedResults<App8Credit> | void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			const { data } = await axiosInst.get<PaginatedResults<App8Credit>>('/auth/credits', {
				params: filterParams
			});
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch the credits change history for a given date range
	 *
	 * @param {string[]} dateRange
	 * @return {Promise<CreditTransaction[] | void>}
	 */
	async getCreditsChangeHistory({}, dateRange: string[]): Promise<CreditTransaction[] | void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			const { data } = await axiosInst.get<CreditTransaction[]>('/auth/credits/history', {
				params: { dateRange: JSON.stringify(dateRange) }
			});
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Assign or edit a credit to a user
	 *
	 * @param {App8Credit} credit
	 * @return {Promise<void>}
	 */
	async saveCredit({}, payload: { id: string|undefined; credit: App8Credit }): Promise<void> {
		const { id, credit } = payload;
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			id ? await axiosInst.put('/auth/credits', credit) : await axiosInst.post('/auth/credits', credit);
		}
		catch (error) {
			// Specific handling for no user found for that email
			if ((error as any)?.response?.data?.code === 832) {
				throw 'user_not_found';
			}
			// Generic error handling
			handleAllErrors(error);
		}
	},

	/**
	 * Update an existing credit
	 *
	 * @param {string} email
	 * @return {Promise<void>}
	 */
	async expireCredit({}, email: string): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			await axiosInst.post('/auth/credits/expire', { email });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async updateAccountingCategories({ commit }, updatedCategories: string[]): Promise<void> {
		const axiosInst: AxiosInstance = state.axiosInst;
		try {
			const { data } = await axiosInst.put<Restaurant>(`/restaurants/update-accounting-categories/${state.tenantId}`, { updatedCategories });
			commit('SET_RESTAURANT', data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	}
};